/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
/**
 * Escapes an email for use it as a firebase key.
 * Scapes dots, $, [, ], /, #. Using url encoding + “dot to comma”
 * Throws error if email is an empty string, is too long, contains invalid characters or reserved substrings.
 * @param email Email to scape.
 * @returns Escaped email.
 */
export function escapeEmail(email: string): string {
	if (!email) {
		throw new Error('Email is required');
	}

	const reserveString = /,%24|%5B|%5D|%2F|%23/;

	if (reserveString.test(email)) {
		throw new Error(`Email contains reserved substrings like ',' '%24' '%5B' '%5D' '%2F' '%23'`);
	}

	const encoder = new TextEncoder();
	const utf8EncodedEmail = encoder.encode(email);

	// Escape invalid characters according to Firebase.
	// "If you create your own keys, they must be UTF-8 encoded, can be a maximum of 768 bytes,
	// and cannot contain ., $, #, [, ], /, or ASCII control characters 0-31 or 127."
	if (utf8EncodedEmail.length > 768) {
		throw new Error(`Email too long: ${utf8EncodedEmail.length} characters`);
	}

	// Throw error if email contains ASCII control characters 0-31 or 127
	const isValitString = !utf8EncodedEmail.some((charCode: number) => {
		return charCode <= 31 || charCode === 127;
	});

	if (!isValitString) {
		throw new Error('Email contains invalid characters');
	}

	const scapedEmail = email
		.toLocaleLowerCase()
		.replace(/\./g, ',') // Replace all dots with commas
		.replace(/\$/g, '%24') // Replace $ with %24
		.replace(/\[/g, '%5B') // Replace [ with %5B
		.replace(/\]/g, '%5D') // Replace ] with %5D
		.replace(/\//g, '%2F') // Replace / with %2F
		.replace(/\#/g, '%23'); // Replace # with %23

	return scapedEmail;
}

/**
 * Unescapes an email that was previously escaped with escapeEmail().
 * @param email Email to unescape.
 * @returns Unescaped email.
 */
export function unEscapeEmail(email: string): string {
	if (!email) {
		throw new Error('Email is required');
	}

	const unescapedEmail = email
		.replace(/,/g, '.') // Replace , with .
		.replace(/%24/g, '$') // Replace %24 with $
		.replace(/%5B/g, '[') // Replace %5B with [
		.replace(/%5D/g, ']') // Replace %5D with ]
		.replace(/%2F/g, '/') // Replace %2F with /
		.replace(/%23/g, '#'); // Replace %23 with #

	return unescapedEmail;
}

//REMOVE below functions when going to proper library solution, this is just created for mobile's use

export function shiftWeekStartToMonday(dayOfWeek: number): number {
	if (dayOfWeek === 0) {
		return 6;
	}
	return dayOfWeek - 1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function concatMaps(map: any, ...iterables: any[]): any {
	if (!iterables || !iterables.length) {
		return map;
	}
	for (const iterable of iterables) {
		if (!iterable) {
			continue;
		}
		for (const item of iterable) {
			map.set(...item);
		}
	}
}

/**
 * Common function to replace special white space (like tabs, returns etc) with spaces.
 * @param text the text to clean
 * @param replaceValue the value to replace the special white space with. Defaults to a space.
 */
export function cleanWhiteSpace(text: string, replaceValue: string = ' '): string {
	return text?.replace(/\s/g, replaceValue);
}
